import { useEffect, useState } from 'react';
import { PAGE_SIZES } from '../components/TablePagination';

export type PageSizeChoice = number | 'auto';

const KEY = 'distribution.pageSize';
const ROW_HEIGHT = 48;
const MIN_ROWS = 5;
const MAX_ROWS = 50;

// How many 48px rows fit below everything else on the page (top bar, paddings, card header,
// filter row, footer and, on the Search page, the KPI strip).
const fitToScreen = (reservedHeight: number) => {
  const rows = Math.floor((window.innerHeight - reservedHeight) / ROW_HEIGHT);
  return Math.min(MAX_ROWS, Math.max(MIN_ROWS, rows));
};

const readChoice = (): PageSizeChoice => {
  try {
    const stored = window.localStorage.getItem(KEY);
    if (!stored || stored === 'auto') return 'auto';
    const n = Number(stored);
    return PAGE_SIZES.includes(n) ? n : 'auto';
  } catch {
    return 'auto';
  }
};

// Rows per page for every list. Defaults to whatever fits the window (and follows resizes);
// a fixed size picked in the selector is remembered in this browser for all lists.
export default function usePageSize(reservedHeight = 270) {
  const [choice, setChoice] = useState<PageSizeChoice>(readChoice);
  const [fitSize, setFitSize] = useState<number>(() =>
    fitToScreen(reservedHeight),
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setFitSize(fitToScreen(reservedHeight)), 150);
    };
    setFitSize(fitToScreen(reservedHeight));
    window.addEventListener('resize', onResize);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [reservedHeight]);

  const setPageSize = (next: PageSizeChoice) => {
    setChoice(next);
    try {
      window.localStorage.setItem(KEY, String(next));
    } catch {
      // private mode or blocked storage: keep it for this session only
    }
  };

  return {
    pageSize: choice === 'auto' ? fitSize : choice,
    setPageSize,
    isAuto: choice === 'auto',
    fitSize,
  };
}
